import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CUBE_COLORS } from './colors.js';
import { MOVES } from './geometric_cube.js';

// Color mapping for cube faces
const COLORS = {
    'U': 0xFFFFFF,    // White
    'R': 0xFF0000,    // Red
    'F': 0x00AA00,    // Green
    'D': 0xFFFF00,    // Yellow
    'L': 0xFFA500,    // Orange
    'B': 0x0000FF,    // Blue
    'X': 0x404040,    // Dark gray
    'o': 0xFDFDC6,    // Light yellow
    'x': 0x8A2BE2,    // Blueviolet
    'y': 0xFF00FF     // Magenta
};

export class RubiksCubeRenderer {
    constructor(container) {
        this.container = container;
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.controls = null;
        this.cubelets = [];
        this.rotationSpeed = Math.PI / 30; // Speed of rotation (radians per frame)
        this.isRotating = false;
        this.rotatingFace = null;
        this.rotatingAxis = null;
        this.rotatingCubelets = [];
        this.cubeletSize = 1.9;  // Changed to match geometric system
        this.cubeletGap = 0.1;   // Gap between cubelets
        this.currentRotation = 0;
        this.targetRotation = 0;
        this.currentState = '';

        this.init();
    }

    init() {
        // Setup renderer
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        this.renderer.setClearColor(0xf0f0f0);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.domElement.style.position = 'absolute';
        this.renderer.domElement.style.top = '0';
        this.renderer.domElement.style.left = '0';
        this.renderer.domElement.style.width = '100%';
        this.renderer.domElement.style.height = '100%';
        this.renderer.domElement.style.zIndex = '1';
        this.container.appendChild(this.renderer.domElement);

        // Setup camera - adjusted for larger cube size
        this.camera.position.set(10, 10, 14);
        this.camera.lookAt(0, 0, 0);

        // Setup controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.screenSpacePanning = false;
        this.controls.minDistance = 6;  // Adjusted for larger cube
        this.controls.maxDistance = 20;  // Adjusted for larger cube

        // Add lighting
        this.setupLighting();

        // Create cube group
        this.cubeGroup = new THREE.Group();
        this.scene.add(this.cubeGroup);

        // Handle window resize
        window.addEventListener('resize', () => this.onWindowResize(), false);
    }

    setupLighting() {
        // Ambient light
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this.scene.add(ambientLight);
        
        // Directional lights
        const directions = [
            { pos: [5, 5, 5], intensity: 0.8 },
            { pos: [-5, -5, -5], intensity: 0.3 },
            { pos: [0, 5, 0], intensity: 0.5 }
        ];
        
        directions.forEach(({ pos, intensity }) => {
            const light = new THREE.DirectionalLight(0xffffff, intensity);
            light.position.set(...pos);
            this.scene.add(light);
        });
    }

    onWindowResize() {
        this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    }

    animate() {
        requestAnimationFrame(() => this.animate());
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }

    async updateCubeState(faceletString) {
        // Store current state
        this.currentState = faceletString;
        
        // Clear existing cube
        while (this.cubeGroup.children.length) {
            this.cubeGroup.remove(this.cubeGroup.children[0]);
        }
        
        // Create new cubelets
        this.createCubelets(faceletString);
        
        // Return a resolved promise since this is synchronous
        return Promise.resolve();
    }

    createCubelets(faceletString) {
        // Create 27 cubelets (3x3x3)
        const positions = [-2, 0, 2];  // All possible positions
        let count = 0;
        for (let x of positions) {
            for (let y of positions) {
                for (let z of positions) {
                    count++;
                    console.log(`Creating cubelet ${count} at position: x=${x}, y=${y}, z=${z}`);
                    this.createCubelet(x, y, z, this.cubeletSize, faceletString);
                }
            }
        }
        console.log(`Total cubelets created: ${count}`);
        console.log(`Current cubeGroup children: ${this.cubeGroup.children.length}`);
    }

    createCubelet(x, y, z, size, faceletString) {
        const geometry = new THREE.BoxGeometry(size, size, size);
        const materials = [];
        
        // Get sticker colors for each face of the cubelet
        const colors = this.getCubeletColors(x, y, z, faceletString);
        
        // Create materials for each face
        for (let i = 0; i < 6; i++) {
            const color = colors[i];
            materials.push(new THREE.MeshPhongMaterial({
                color: color,
                shininess: 30,
                specular: 0x444444
            }));
        }
        
        const cubelet = new THREE.Mesh(geometry, materials);
        cubelet.position.set(x, y, z);
        // Add position data to userData for debugging
        cubelet.userData.gridPosition = { x, y, z };
        this.cubeGroup.add(cubelet);
    }

    getCubeletColors(x, y, z, faceletString) {
        const colors = Array(6).fill(0x333333); // Default dark gray
        const eps = 0.1; // Small epsilon for floating point comparison
        
        // Helper to get face index from coordinates
        const getFaceIndex = (face, row, col) => {
            const faceOffset = {
                U: 0, R: 1, F: 2, D: 3, L: 4, B: 5
            }[face] * 9;
            return faceOffset + row * 3 + col;
        };
        
        // Check each face - adjusted for -2 to 2 coordinate system
        if (Math.abs(y - 2) < eps) { // Up face
            const row = Math.floor((z/2 + 1));
            const col = Math.floor((x/2 + 1));
            colors[2] = COLORS[faceletString[getFaceIndex('U', row, col)]];
        }
        if (Math.abs(y + 2) < eps) { // Down face
            const row = Math.floor((-z/2 + 1));  // Changed to -z/2 to fix rendering order
            const col = Math.floor((x/2 + 1));
            colors[3] = COLORS[faceletString[getFaceIndex('D', row, col)]];
        }
        if (Math.abs(z - 2) < eps) { // Front face
            const row = Math.floor((-y/2 + 1));
            const col = Math.floor((x/2 + 1));
            colors[4] = COLORS[faceletString[getFaceIndex('F', row, col)]];
        }
        if (Math.abs(z + 2) < eps) { // Back face
            const row = Math.floor((-y/2 + 1));
            const col = Math.floor((-x/2 + 1));
            colors[5] = COLORS[faceletString[getFaceIndex('B', row, col)]];
        }
        if (Math.abs(x - 2) < eps) { // Right face
            const row = Math.floor((-y/2 + 1));
            const col = Math.floor((-z/2 + 1));
            colors[0] = COLORS[faceletString[getFaceIndex('R', row, col)]];
        }
        if (Math.abs(x + 2) < eps) { // Left face
            const row = Math.floor((-y/2 + 1));
            const col = Math.floor((z/2 + 1));
            colors[1] = COLORS[faceletString[getFaceIndex('L', row, col)]];
        }
        
        return colors;
    }

    render() {
        if (this.isRotating && this.rotatingCubelets.length > 0) {
            // Increment rotation
            this.currentRotation += this.rotationSpeed;
            
            // Apply rotation to rotating cubelets
            const axis = this.rotatingAxis;
            const quaternion = new THREE.Quaternion();
            quaternion.setFromAxisAngle(
                new THREE.Vector3(
                    axis.x === 'x' ? 1 : 0,
                    axis.y === 'y' ? 1 : 0,
                    axis.z === 'z' ? 1 : 0
                ),
                this.rotationSpeed
            );
            
            this.rotatingCubelets.forEach(cubelet => {
                if (cubelet) {
                    cubelet.quaternion.premultiply(quaternion);
                }
            });
        }

        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }

    async rotateFace(face, direction, is180Degree = false) {
        return new Promise((resolve, reject) => {
            if (this.isRotating) {
                reject(new Error('Rotation already in progress'));
                return;
            }

            this.isRotating = true;
            console.log(`\nStarting rotation of face ${face}`);
            console.log(`Total cubelets in cube: ${this.cubeGroup.children.length}`);
            
            // Create temporary rotation group
            const rotationGroup = new THREE.Group();
            this.scene.add(rotationGroup);
            
            // Get cubelets that need to rotate
            const rotatingCubelets = [];
            
            // Create a copy of the children array to prevent modification during iteration
            const cubelets = [...this.cubeGroup.children];
            
            cubelets.forEach((cubelet, index) => {
                const pos = cubelet.position;
                console.log(`\nChecking cubelet ${index}:`);
                console.log(`Position: x=${pos.x}, y=${pos.y}, z=${pos.z}`);
                if (this.isCubeletInFace(pos, face)) {
                    console.log(`Selected for rotation: cubelet at x=${pos.x}, y=${pos.y}, z=${pos.z}`);
                    // Save original position
                    cubelet.userData.originalParent = cubelet.parent;
                    cubelet.userData.originalPosition = pos.clone();
                    
                    // Add to rotation group
                    rotationGroup.attach(cubelet);
                    rotatingCubelets.push(cubelet);
                }
            });

            console.log(`\nRotation Summary for face ${face}:`);
            console.log(`Total cubelets selected for rotation: ${rotatingCubelets.length}`);
            console.log('Selected cubelet positions:');
            rotatingCubelets.forEach((cubelet, index) => {
                const pos = cubelet.position;
                console.log(`${index + 1}: x=${pos.x}, y=${pos.y}, z=${pos.z}`);
            });

            // Set rotation axis and direction
            const axis = this.getFaceRotationAxis(face);
            rotationGroup.position.set(0, 0, 0); // Rotation center at origin
            
            // Animation parameters
            const duration = is180Degree ? 400 : 300; // longer duration for 180-degree turns
            // Adjust rotation direction based on face and move direction
            let rotationDirection = -direction;
            switch(face) {
                case 'U':
                case 'R':
                case 'F':
                    // Standard faces: keep direction as is
                    break;
                case 'D':
                case 'L':
                case 'B':
                    // Opposite faces: invert direction
                    rotationDirection = direction;
                    break;
            }
            const totalAngle = Math.PI * (is180Degree ? 1 : 0.5) * Math.sign(rotationDirection);
            let startTime = null;
            
            // Animation frame function
            const animate = (currentTime) => {
                if (!startTime) startTime = currentTime;
                const progress = (currentTime - startTime) / duration;
                
                if (progress < 1) {
                    // Calculate current rotation angle
                    const currentAngle = totalAngle * progress;
                    
                    // Set rotation group's rotation
                    rotationGroup.setRotationFromAxisAngle(axis, currentAngle);
                    
                    // Continue animation
                    requestAnimationFrame(animate);
                } else {
                    // Animation complete, return cubelets to original group
                    rotatingCubelets.forEach(cubelet => {
                        this.cubeGroup.attach(cubelet);
                    });
                    
                    // Remove temporary rotation group
                    this.scene.remove(rotationGroup);
                    
                    // Update cube state
                    this.updateCubeState(this.currentState);
                    
                    this.isRotating = false;
                    resolve();
                }
                
                // Render scene
                this.renderer.render(this.scene, this.camera);
            };
            
            // Start animation
            requestAnimationFrame(animate);
        });
    }

    getFaceRotationAxis(face) {
        // Define axes for standard Rubik's cube rotation
        const axes = {
            'U': new THREE.Vector3(0, 1, 0),    // Y axis (looking from top)
            'D': new THREE.Vector3(0, 1, 0),    // Y axis (looking from bottom)
            'R': new THREE.Vector3(1, 0, 0),    // X axis (looking from right)
            'L': new THREE.Vector3(1, 0, 0),    // X axis (looking from left)
            'F': new THREE.Vector3(0, 0, 1),    // Z axis (looking from front)
            'B': new THREE.Vector3(0, 0, 1)     // Z axis (looking from back)
        };
        return axes[face];
    }

    isCubeletInFace(position, face) {
        const eps = 0.1; // Floating point comparison epsilon
        const result = (() => {
            switch (face) {
                case 'U': return Math.abs(position.y - 2) < eps;  // y = 2 plane
                case 'D': return Math.abs(position.y + 2) < eps;  // y = -2 plane
                case 'R': return Math.abs(position.x - 2) < eps;  // x = 2 plane
                case 'L': return Math.abs(position.x + 2) < eps;  // x = -2 plane
                case 'F': return Math.abs(position.z - 2) < eps;  // z = 2 plane
                case 'B': return Math.abs(position.z + 2) < eps;  // z = -2 plane
                default: return false;
            }
        })();
        console.log(`Checking ${face} face: position=${JSON.stringify(position)}, result=${result}`);
        return result;
    }
} 